import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  User,
  Contact,
  Matter,
  DocumentMeta,
  PagedResponse,
  MatterTimelineResponse,
  BillingEventsResponse,
  BillingEvent,
  AssistantApiResponse,
  AssistantQueryRequest,
  Conversation,
  ConversationListItem,
  AdminRole,
  AdminPermission,
  InviteCode,
} from '../models';
import { RegisterRequest, RegisterWithCodeRequest, RegisterCreateTenantRequest, RegisterResponse, LoginRequest, LoginResponse, MfaSetupResponse } from '../models/auth.model';
import { TimelineEvent, TimelineEventCreateRequest, TimelineEventUpdateRequest } from '../models/crm.model';
import { ConflictCheckResult } from '../models/conflict.model';
import {
  TimeEntry, TimeEntryListResponse, CreateTimeEntryPayload, UpdateTimeEntryPayload,
  Expense, ExpenseListResponse, CreateExpensePayload, UpdateExpensePayload,
  Invoice, InvoiceListResponse, CreateInvoicePayload, UpdateInvoicePayload,
  MatterBillingSummary, RetainerAccount,
} from '../models/billing.model';
import { Task, CreateTaskRequest } from '../models/task.model';
import { CalendarEvent, CalendarEventsResponse } from '../models/calendar-event.model';
import { FilingTemplate, FilingTemplatesResponse, CreateFilingTemplateRequest, UpdateFilingTemplateRequest } from '../models/filing-checklist.model';
import { DeadlineRule, DeadlineRulesResponse } from '../models/deadline-rule.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private cleanParams(params: Record<string, any>): Record<string, string | number | boolean> {
    const clean: Record<string, string | number | boolean> = {};
    for (const key of Object.keys(params)) {
      const val = params[key];
      if (val !== undefined && val !== null && val !== 'undefined') {
        clean[key] = val;
      }
    }
    return clean;
  }

  // Auth Endpoints
  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register`, data);
  }
  registerWithCode(data: RegisterWithCodeRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register-with-code`, data);
  }
  registerCreateTenant(data: RegisterCreateTenantRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register-create-tenant`, data);
  }
  validateInviteCode(code: string): Observable<{ valid: boolean; message?: string }> {
    return this.http.get<{ valid: boolean; message?: string }>(`${this.apiUrl}/auth/validate-code/${code}`);
  }
  login(data: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, data);
  }
  refreshToken(refreshToken: string): Observable<{ accessToken: string; refreshToken: string }> {
    return this.http.post<{ accessToken: string; refreshToken: string }>(
      `${this.apiUrl}/auth/refresh`,
      { refreshToken },
    );
  }
  logout(refreshToken: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/logout`, { refreshToken });
  }
  getProfile(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/auth/profile`);
  }
  enableMfa(): Observable<MfaSetupResponse> {
    return this.http.post<MfaSetupResponse>(`${this.apiUrl}/auth/mfa/enable`, {});
  }
  confirmMfa(code: string): Observable<{ success: boolean; backupCodes?: string[] }> {
    return this.http.post<{ success: boolean; backupCodes?: string[] }>(`${this.apiUrl}/auth/mfa/confirm`, { code });
  }

  // User Endpoints
  getUsers(params?: { skip?: number; limit?: number; search?: string; includeInactive?: boolean }): Observable<{ data: User[]; total: number; skip: number; limit: number }> {
    return this.http.get<{ data: User[]; total: number; skip: number; limit: number }>(`${this.apiUrl}/users`, {
      params: {
        ...(params?.skip !== undefined ? { skip: params.skip } : {}),
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.includeInactive ? { includeInactive: true } : {}),
      },
    });
  }

  updateUserRoles(userId: string, roles: string[]): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/users/${userId}/roles`, { roles });
  }

  setUserStatus(userId: string, isActive: boolean): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/users/${userId}/status`, { isActive });
  }

  // Admin — Roles Endpoints
  getAdminRoles(): Observable<AdminRole[]> {
    return this.http.get<AdminRole[]>(`${this.apiUrl}/admin/roles`);
  }

  createAdminRole(data: { name: string; description?: string; permissions: string[] }): Observable<AdminRole> {
    return this.http.post<AdminRole>(`${this.apiUrl}/admin/roles`, data);
  }

  updateAdminRole(id: string, data: { name?: string; description?: string; permissions?: string[]; isActive?: boolean }): Observable<AdminRole> {
    return this.http.patch<AdminRole>(`${this.apiUrl}/admin/roles/${id}`, data);
  }

  deleteAdminRole(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.apiUrl}/admin/roles/${id}`);
  }

  // Admin — Permissions Endpoint
  getAdminPermissions(): Observable<AdminPermission[]> {
    return this.http.get<AdminPermission[]>(`${this.apiUrl}/admin/permissions`);
  }

  // Admin — Invite Code Endpoints (existing auth endpoints, admin-gated)
  getInviteCodes(): Observable<{ codes: InviteCode[]; total: number }> {
    return this.http.get<{ codes: InviteCode[]; total: number }>(`${this.apiUrl}/auth/invite-codes`);
  }

  createInviteCode(data: { maxUses?: number; expiresInDays?: number }): Observable<InviteCode> {
    return this.http.post<InviteCode>(`${this.apiUrl}/auth/invite-codes`, data);
  }

  revokeInviteCode(code: string): Observable<{ message: string; code: string }> {
    return this.http.delete<{ message: string; code: string }>(`${this.apiUrl}/auth/invite-codes/${code}`);
  }

  // Contact Endpoints
  getContacts(params?: { skip?: number; limit?: number; search?: string; type?: string }): Observable<PagedResponse<Contact>> {
    return this.http.get<PagedResponse<Contact>>(`${this.apiUrl}/contacts`, {
      params: {
        ...(params?.skip !== undefined ? { skip: params.skip } : {}),
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.type ? { type: params.type } : {}),
      },
    });
  }
  getContact(id: string): Observable<Contact> {
    return this.http.get<Contact>(`${this.apiUrl}/contacts/${id}`);
  }
  createContact(contact: Partial<Contact>): Observable<Contact> {
    return this.http.post<Contact>(`${this.apiUrl}/contacts`, contact);
  }
  updateContact(id: string, contact: Partial<Contact>): Observable<Contact> {
    return this.http.patch<Contact>(`${this.apiUrl}/contacts/${id}`, contact);
  }
  deleteContact(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.apiUrl}/contacts/${id}`);
  }

  // Matter Endpoints
  getMatters(params?: { skip?: number; limit?: number; status?: string; contactId?: string; search?: string }): Observable<PagedResponse<Matter>> {
    return this.http.get<PagedResponse<Matter>>(`${this.apiUrl}/matters`, {
      params: {
        ...(params?.skip !== undefined ? { skip: params.skip } : {}),
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contactId ? { contactId: params.contactId } : {}),
      },
    });
  }
  getMattersByContact(contactId: string): Observable<Matter[]> {
    return this.http.get<Matter[]>(`${this.apiUrl}/matters/contact/${contactId}`);
  }
  getMatter(id: string): Observable<Matter> {
    return this.http.get<Matter>(`${this.apiUrl}/matters/${id}`);
  }
  createMatter(matter: Partial<Matter>): Observable<Matter> {
    return this.http.post<Matter>(`${this.apiUrl}/matters`, matter);
  }
  updateMatter(id: string, matter: Partial<Matter>): Observable<Matter> {
    return this.http.patch<Matter>(`${this.apiUrl}/matters/${id}`, matter);
  }
  deleteMatter(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.apiUrl}/matters/${id}`);
  }
  getMatterTimeline(id: string, params?: { cursor?: string; order?: 'asc' | 'desc'; limit?: number }): Observable<MatterTimelineResponse> {
    return this.http.get<MatterTimelineResponse>(`${this.apiUrl}/matters/${id}/timeline`, {
      params: {
        ...(params?.cursor ? { cursor: params.cursor } : {}),
        ...(params?.order ? { order: params.order } : {}),
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      },
    });
  }
  createMatterTimelineEvent(
    matterId: string,
    event: TimelineEventCreateRequest,
  ): Observable<TimelineEvent> {
    return this.http.post<TimelineEvent>(`${this.apiUrl}/matters/${matterId}/timeline-events`, event);
  }
  updateMatterTimelineEvent(
    matterId: string,
    eventId: string,
    event: TimelineEventUpdateRequest,
  ): Observable<TimelineEvent> {
    return this.http.patch<TimelineEvent>(`${this.apiUrl}/matters/${matterId}/timeline-events/${eventId}`, event);
  }
  archiveMatterTimelineEvent(matterId: string, eventId: string): Observable<TimelineEvent> {
    return this.http.post<TimelineEvent>(`${this.apiUrl}/matters/${matterId}/timeline-events/${eventId}/archive`, {});
  }
  deleteMatterTimelineEvent(matterId: string, eventId: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.apiUrl}/matters/${matterId}/timeline-events/${eventId}`);
  }
  createBillingEvent(matterId: string, billingEvent: { amount: number; currency: string; eventDate: string; description: string; type?: string }): Observable<BillingEvent> {
    return this.http.post<BillingEvent>(`${this.apiUrl}/matters/${matterId}/billing-events`, billingEvent);
  }
  getBillingEvents(matterId: string): Observable<BillingEventsResponse> {
    return this.http.get<BillingEventsResponse>(`${this.apiUrl}/matters/${matterId}/billing-events`);
  }

  // Document Endpoints
  getDocuments(): Observable<DocumentMeta[]> {
    return this.http.get<DocumentMeta[]>(`${this.apiUrl}/documents`);
  }
  getDocumentsByMatter(matterId: string): Observable<DocumentMeta[]> {
    return this.http.get<DocumentMeta[]>(`${this.apiUrl}/documents/matter/${matterId}`);
  }
  getDocument(id: string): Observable<DocumentMeta> {
    return this.http.get<DocumentMeta>(`${this.apiUrl}/documents/${id}`);
  }
  getDocumentContent(id: string): Observable<{ content: string; source: string; mimeType?: string }> {
    return this.http.get<{ content: string; source: string; mimeType?: string }>(`${this.apiUrl}/documents/${id}/content`);
  }
  uploadDocument(formData: FormData): Observable<DocumentMeta> {
    return this.http.post<DocumentMeta>(`${this.apiUrl}/documents/upload`, formData);
  }
  uploadDocumentForAI(formData: FormData): Observable<{ documentId: string }> {
    return this.http.post<{ documentId: string }>(`${this.apiUrl}/ai/documents/upload-for-ai`, formData);
  }
  updateDocument(id: string, doc: Partial<DocumentMeta>): Observable<DocumentMeta> {
    return this.http.put<DocumentMeta>(`${this.apiUrl}/documents/${id}`, doc);
  }
  deleteDocument(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documents/${id}`);
  }
  purgeAiDocumentByFilename(filename: string): Observable<{ deletedChunks: number; deletedVectors: number }> {
    return this.http.delete<{ deletedChunks: number; deletedVectors: number }>(
      `${this.apiUrl}/ai/documents/purge`,
      { params: { filename } }
    );
  }
  createDocumentVersion(id: string, formData: FormData): Observable<DocumentMeta> {
    return this.http.post<DocumentMeta>(`${this.apiUrl}/documents/${id}/version`, formData);
  }

  // Conflict Checker Endpoints
  checkConflicts(
    names: string[],
    threshold?: number,
    topK?: number,
  ): Observable<ConflictCheckResult[]> {
    const body: Record<string, unknown> = { names };
    if (threshold !== undefined) body['threshold'] = threshold;
    if (topK !== undefined) body['topK'] = topK;
    return this.http
      .post<{ checked: number; conflictsFound: number; results: ConflictCheckResult[] }>(
        `${this.apiUrl}/conflicts/check`,
        body,
      )
      .pipe(map((r) => r.results));
  }

  getConflictIndexStats(): Observable<{ totalEntities: number; partitions: number }> {
    return this.http.get<{ totalEntities: number; partitions: number }>(
      `${this.apiUrl}/conflicts/index/stats`,
    );
  }

  // Billing — Time Entries
  getTimeEntries(params: { matterId?: string; userId?: string; isBilled?: boolean; dateFrom?: string; dateTo?: string; skip?: number; limit?: number }): Observable<TimeEntryListResponse> {
    return this.http.get<TimeEntryListResponse>(`${this.apiUrl}/billing/time-entries`, { params: this.cleanParams(params) });
  }
  createTimeEntry(payload: CreateTimeEntryPayload): Observable<TimeEntry> {
    return this.http.post<TimeEntry>(`${this.apiUrl}/billing/time-entries`, payload);
  }
  updateTimeEntry(id: string, payload: UpdateTimeEntryPayload): Observable<TimeEntry> {
    return this.http.patch<TimeEntry>(`${this.apiUrl}/billing/time-entries/${id}`, payload);
  }
  deleteTimeEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/billing/time-entries/${id}`);
  }

  // Billing — Expenses
  getExpenses(params: { matterId?: string; isBilled?: boolean; skip?: number; limit?: number }): Observable<ExpenseListResponse> {
    return this.http.get<ExpenseListResponse>(`${this.apiUrl}/billing/expenses`, { params: this.cleanParams(params) });
  }
  createExpense(payload: CreateExpensePayload): Observable<Expense> {
    return this.http.post<Expense>(`${this.apiUrl}/billing/expenses`, payload);
  }
  createExpenseUpload(payload: object): Observable<Expense> {
    return this.http.post<Expense>(`${this.apiUrl}/billing/expenses`, payload);
  }
  updateExpense(id: string, payload: UpdateExpensePayload): Observable<Expense> {
    return this.http.patch<Expense>(`${this.apiUrl}/billing/expenses/${id}`, payload);
  }
  deleteExpense(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/billing/expenses/${id}`);
  }

  // Billing — Invoices
  getInvoices(params: { matterId?: string; status?: string; skip?: number; limit?: number }): Observable<InvoiceListResponse> {
    return this.http.get<InvoiceListResponse>(`${this.apiUrl}/billing/invoices`, { params: this.cleanParams(params) });
  }
  createInvoice(payload: CreateInvoicePayload): Observable<Invoice> {
    return this.http.post<Invoice>(`${this.apiUrl}/billing/invoices`, payload);
  }
  updateInvoice(id: string, payload: UpdateInvoicePayload): Observable<Invoice> {
    return this.http.patch<Invoice>(`${this.apiUrl}/billing/invoices/${id}`, payload);
  }
  getMatterBillingSummary(matterId: string): Observable<MatterBillingSummary> {
    return this.http.get<MatterBillingSummary>(`${this.apiUrl}/billing/matters/${matterId}/summary`);
  }

  // Billing — Retainer
  getRetainerAccount(matterId: string): Observable<RetainerAccount> {
    return this.http.get<RetainerAccount>(`${this.apiUrl}/billing/retainer/${matterId}`);
  }
  depositRetainer(matterId: string, amount: number, description: string): Observable<RetainerAccount> {
    return this.http.post<RetainerAccount>(`${this.apiUrl}/billing/retainer/${matterId}/deposit`, { amount, description });
  }

  // Billing — Reports
  getBillingRevenueReport(groupBy?: string): Observable<any> {
    const params: Record<string, string> = {};
    if (groupBy) params['groupBy'] = groupBy;
    return this.http.get<any>(`${this.apiUrl}/billing/reports/revenue`, { params });
  }
  getBillingAgingReport(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/billing/reports/aging`);
  }
  getBillingProductivityReport(attorneyId?: string, month?: string): Observable<any> {
    const params: Record<string, string> = {};
    if (attorneyId) params['attorneyId'] = attorneyId;
    if (month) params['month'] = month;
    return this.http.get<any>(`${this.apiUrl}/billing/reports/productivity`, { params });
  }

  // Deadline Rules
  getDeadlineRules(params?: { includeInactive?: boolean; includeSystem?: boolean }): Observable<DeadlineRulesResponse> {
    return this.http.get<DeadlineRulesResponse>(`${this.apiUrl}/deadline-rules`, { params: this.cleanParams(params ?? {}) });
  }
  createDeadlineRule(payload: Partial<DeadlineRule>): Observable<DeadlineRule> {
    return this.http.post<DeadlineRule>(`${this.apiUrl}/deadline-rules`, payload);
  }
  updateDeadlineRule(id: string, payload: Partial<DeadlineRule>): Observable<DeadlineRule> {
    return this.http.patch<DeadlineRule>(`${this.apiUrl}/deadline-rules/${id}`, payload);
  }
  deleteDeadlineRule(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/deadline-rules/${id}`);
  }

  // Filing Templates
  getFilingTemplates(activeOnly?: boolean): Observable<FilingTemplatesResponse> {
    const params: Record<string, string> = {};
    if (activeOnly !== undefined) params['activeOnly'] = String(activeOnly);
    return this.http.get<FilingTemplatesResponse>(`${this.apiUrl}/filing-templates`, { params });
  }
  createFilingTemplate(payload: CreateFilingTemplateRequest): Observable<FilingTemplate> {
    return this.http.post<FilingTemplate>(`${this.apiUrl}/filing-templates`, payload);
  }
  updateFilingTemplate(id: string, payload: UpdateFilingTemplateRequest): Observable<FilingTemplate> {
    return this.http.patch<FilingTemplate>(`${this.apiUrl}/filing-templates/${id}`, payload);
  }
  deleteFilingTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/filing-templates/${id}`);
  }

  // Tasks
  getTasks(params?: { status?: string; assignedTo?: string; matterId?: string; skip?: number; limit?: number }): Observable<{ data: Task[] }> {
    return this.http.get<{ data: Task[] }>(`${this.apiUrl}/tasks`, { params: this.cleanParams(params ?? {}) });
  }
  createTask(payload: CreateTaskRequest): Observable<Task> {
    return this.http.post<Task>(`${this.apiUrl}/tasks`, payload);
  }
  updateTask(id: string, payload: Partial<Task>): Observable<Task> {
    return this.http.patch<Task>(`${this.apiUrl}/tasks/${id}`, payload);
  }
  deleteTask(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${id}`);
  }

  // Calendar
  getCalendarEvents(params: { start: string; end: string; matterId?: string }): Observable<CalendarEventsResponse> {
    return this.http.get<CalendarEventsResponse>(`${this.apiUrl}/calendar`, { params: this.cleanParams(params) });
  }
  createCalendarEvent(payload: Partial<CalendarEvent>): Observable<CalendarEvent> {
    return this.http.post<CalendarEvent>(`${this.apiUrl}/calendar`, payload);
  }
  updateCalendarEvent(id: string, payload: Partial<CalendarEvent>): Observable<CalendarEvent> {
    return this.http.patch<CalendarEvent>(`${this.apiUrl}/calendar/${id}`, payload);
  }
  deleteCalendarEvent(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/calendar/${id}`);
  }
  downloadCalendarIcs(): string {
    return `${this.apiUrl}/calendar/export.ics`;
  }

  // Health Check
  healthCheck(): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.apiUrl}/health`);
  }

  // Assistant Endpoints
  queryAssistant(payload: AssistantQueryRequest): Observable<AssistantApiResponse> {
    return this.http.post<AssistantApiResponse>(`${this.apiUrl}/ai/chat`, payload);
  }

  // Conversation Endpoints
  listConversations(matterId?: string, contactId?: string, includeArchived?: boolean): Observable<ConversationListItem[]> {
    const params: Record<string, string | boolean> = {};
    if (matterId) params['matterId'] = matterId;
    if (contactId) params['contactId'] = contactId;
    if (includeArchived !== undefined) params['includeArchived'] = includeArchived;
    return this.http.get<ConversationListItem[]>(`${this.apiUrl}/conversations`, { params });
  }
  getConversation(conversationId: string): Observable<Conversation> {
    return this.http.get<Conversation>(`${this.apiUrl}/conversations/${conversationId}`);
  }
  createConversation(matterId?: string, contactId?: string, title?: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.apiUrl}/conversations`, { matterId, contactId, title });
  }
  updateConversation(conversationId: string, updates: { title?: string; isArchived?: boolean; matterId?: string; contactId?: string }): Observable<Conversation> {
    return this.http.patch<Conversation>(`${this.apiUrl}/conversations/${conversationId}`, updates);
  }
  archiveConversation(conversationId: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/archive`, {});
  }
  deleteConversation(conversationId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/conversations/${conversationId}`);
  }
}
